/**
 * The original Trend profile: steel-blue brand, orange secondary, Outfit type,
 * pronounced elevation. Kept intact so switching back is a one-line change in
 * profiles/index.js.
 */
export const trendProfile = {
  name: 'trend',

  brand: {
    couleur_primaire: '#247cb1',
    couleur_secondaire: '#f59b4a',
    couleur_accent: '#4cc9f0',
  },

  /** Alpha behind `--primary-soft`. */
  softAlpha: 0.08,

  scales: {
    font: {
      sans: "'Outfit', 'Acumin Variable Concept', 'Manrope', sans-serif",
      heading: "'Outfit', 'Acumin Variable Concept', 'Manrope', sans-serif",
      mono: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
      label: "'Outfit', 'Acumin Variable Concept', 'Manrope', sans-serif",
    },
    fontSize: {
      label: '0.75rem',
      xs: '0.75rem',
      sm: '0.8125rem',
      base: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      heading: '600',
      label: '600',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.7',
    },
    letterSpacing: {
      tight: '0',
      normal: '0',
      wide: '0.02em',
      label: '0.04em',
    },
    radius: {
      xs: '4px',
      sm: '8px',
      md: '12px',
      lg: '20px',
      xl: '20px',
      full: '9999px',
    },
    space: {
      0: '0px',
      1: '4px',
      2: '8px',
      3: '12px',
      4: '16px',
      5: '20px',
      6: '24px',
      8: '32px',
      10: '40px',
      12: '48px',
    },
    shadow: {
      xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
      sm: '0 2px 8px rgba(0, 0, 0, 0.05)',
      md: '0 8px 16px rgba(0, 0, 0, 0.08)',
      lg: '0 12px 24px rgba(0, 0, 0, 0.12)',
      xl: '0 20px 40px rgba(0, 0, 0, 0.16)',
      card: '0 2px 8px rgba(0, 0, 0, 0.05)',
      elevated: '0 12px 24px rgba(0, 0, 0, 0.12)',
    },
    gray: {
      50: '#ffffff',
      100: '#f5f5f5',
      200: '#fafafa',
      300: '#f0f0f0',
      400: '#d9d9d9',
      500: '#bfbfbf',
      600: '#8c8c8c',
      700: '#666666',
      800: '#434343',
      900: '#1f1f1f',
    },
    // The `*Ink` variants exist so both profiles share a shape; Trend used its
    // base colours as ink, so they stay equal here.
    semantic: {
      error: '#ff4d4f',
      errorInk: '#ff4d4f',
      errorBg: '#fff2f0',
      errorBorder: '#ffccc7',
      warning: '#faad14',
      warningInk: '#faad14',
      warningBg: '#fffbe6',
      warningBorder: '#ffe58f',
      warningOrange: '#fa8c16',
      warningOrangeBg: '#fff7e6',
      warningOrangeBorder: '#ffd591',
      success: '#52c41a',
      successInk: '#52c41a',
      successBg: '#f6ffed',
      successBorder: '#b7eb8f',
      info: '#1890ff',
      infoInk: '#1890ff',
      infoBg: '#e6f7ff',
      infoBorder: '#91d5ff',
    },
    surface: {
      page: '#f8f9fa',
      canvas: 'rgb(248, 250, 252)',
      card: '#ffffff',
      cardGlass: 'rgba(255, 255, 255, 0.95)',
      glassBorder: 'rgba(255, 255, 255, 0.5)',
      sidebar: '#ffffff',
      overlay: 'rgba(0, 0, 0, 0.45)',
    },
    text: {
      dark: '#2b2d42',
      light: '#8d99ae',
      heading: '#1f2937',
      body: '#4b5563',
      muted: '#6b7280',
      inverse: '#ffffff',
    },
    border: {
      default: '#e5e7eb',
      subtle: '#f3f4f6',
      strong: '#d9d9d9',
    },
    /** Trend's fields were a white box with a grey border. */
    field: {
      bg: '#ffffff',
      border: '#e5e7eb',
      borderFocus: '#247cb1',
      ring: 'rgba(36, 124, 177, 0.4)',
    },
    control: {
      height: '44px',
      heightSm: '32px',
      heightLg: '52px',
      paddingX: '15px',
      paddingY: '10px',
      buttonPaddingX: '15px',
      buttonPaddingXSm: '10px',
      buttonPaddingXLg: '20px',
    },
    /** Trend's shell: a 24px-padded page with no max width and a 200px sidebar. */
    layout: {
      pagePadding: '24px',
      pagePaddingMobile: '16px',
      contentMaxWidth: 'none',
      sectionGap: '24px',
      cardPadding: '24px',
      headerHeight: '64px',
      sidebarWidth: '200px',
      sidebarWidthCollapsed: '80px',
      sidebarWidthMobile: '280px',
      tableRowHeight: '52px',
    },
    duration: {
      fast: '120ms',
      normal: '200ms',
      slow: '320ms',
    },
    easing: {
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    },
    zIndex: {
      base: '1',
      sticky: '100',
      header: '1100',
      popup: '1200',
      modal: '2000',
      toast: '2100',
    },
  },

  /** Chrome that is derived from the brand rather than fixed. */
  buildComponent: ({ primary, secondary, accent, scales, withAlpha, readableTextOn }) => ({
    tableHeaderBg: secondary,
    tableHeaderColor: readableTextOn(secondary),
    tableBodyBg: withAlpha(primary, 0.08),
    drawerHeaderBg: withAlpha(primary, 0.08),
    footerBg: scales.surface.card,
    gradientBrand: `linear-gradient(135deg, ${primary}, ${secondary})`,
    glowShadow: `0 0 50px -10px ${withAlpha(accent, 0.3)}`,
    pageGlow: 'none',
  }),
}

export default trendProfile
